(function () {
  if (customElements.get("upw-slider")) return;

  class UpwSlider extends HTMLElement {
    #products = [];
    #initialized = false;
    #resizeObserver = null;
    #handleResize = () => {
      this.#syncLayout();
      this.#updateNav();
    };
    #handleScroll = () => this.#updateNav();

    connectedCallback() {
      if (this.#initialized) return;
      this.productId = this.dataset.productId;
      this.cartIds = (this.dataset.cartIds || "")
        .split(",")
        .map(Number)
        .filter(Boolean);
      this.#waitForInit();
    }

    disconnectedCallback() {
      if (this.trackOuter) {
        this.trackOuter.removeEventListener("scroll", this.#handleScroll);
      }
      if (this.#resizeObserver) {
        this.#resizeObserver.disconnect();
      }
      window.removeEventListener("resize", this.#handleResize);
    }

    #waitForInit(attempt = 0) {
      if (!this.isConnected || attempt > 40 || this.#initialized) return;
      if (
        this.querySelector(".upw__track") &&
        this.querySelector(".upw__arrow--prev") &&
        this.querySelector(".upw__arrow--next")
      ) {
        this.#init();
      } else {
        setTimeout(() => this.#waitForInit(attempt + 1), 50);
      }
    }

    #init() {
      this.#initialized = true;
      this.trackOuter = this.querySelector(".upw__track-outer");
      this.track = this.querySelector(".upw__track");
      this.prevBtn = this.querySelector(".upw__arrow--prev");
      this.nextBtn = this.querySelector(".upw__arrow--next");

      if (!this.track || !this.trackOuter || !this.prevBtn || !this.nextBtn) return;

      this.prevBtn.addEventListener("click", () => this.#navigate(-1));
      this.nextBtn.addEventListener("click", () => this.#navigate(1));
      this.trackOuter.addEventListener("scroll", this.#handleScroll, {
        passive: true,
      });
      window.addEventListener("resize", this.#handleResize);
      if ("ResizeObserver" in window) {
        this.#resizeObserver = new ResizeObserver(() => this.#handleResize());
        this.#resizeObserver.observe(this.trackOuter);
      }

      this.addEventListener("change", (event) => {
        const select = event.target.closest(".upw__select");
        if (select) {
          event.stopPropagation();
          this.#handleSelect(select);
        }
      });

      this.track.addEventListener("click", (event) => {
        const button = event.target.closest(".upw__add");
        if (button && !button.disabled) this.#addItem(button);
      });

      if (this.productId) this.#load();
    }

    async #load() {
      try {
        let products = await this.#fetchIntent("complementary");
        if (!products.length) products = await this.#fetchIntent("related");

        this.#products = products
          .filter((product) => !this.cartIds.includes(product.id))
          .slice(0, 6);

        if (!this.#products.length) {
          this.remove();
          return;
        }

        this.track.innerHTML = this.#products
          .map((product) => this.#renderCard(product))
          .join("");

        requestAnimationFrame(() => {
          this.#syncLayout();
          this.#updateNav();
        });
      } catch {
        this.remove();
      }
    }

    #cards() {
      return [...this.track.querySelectorAll(".upw__card")];
    }

    #renderCard(product) {
      const image = product.featured_image;
      const source = image ? (image.url || image.src || image) + "&width=240" : "";
      const alt = (typeof image === "object" ? image.alt : "") || product.title;

      const defaultVariant =
        product.variants.find((variant) => variant.available) || product.variants[0];
      const price = this.#formatPrice(defaultVariant.price);

      const variantsJson = JSON.stringify(
        product.variants.map((variant) => ({
          id: variant.id,
          options: variant.options,
          price: variant.price,
          available: variant.available,
        })),
      ).replace(/"/g, "&quot;");

      let selectsHtml = "";
      if (product.variants.length > 1) {
        const optionCount = defaultVariant.options?.length || 0;
        const selects = [];

        for (let index = 0; index < optionCount; index += 1) {
          const values = [...new Set(product.variants.map((variant) => variant.options[index]))];
          if (values.length <= 1) continue;

          const options = values
            .map((value) => {
              const selected = defaultVariant.options[index] === value ? " selected" : "";
              return `<option value="${value.replace(/"/g, "&quot;")}"${selected}>${value}</option>`;
            })
            .join("");

          selects.push(`<select class="upw__select" data-opt="${index}">${options}</select>`);
        }

        if (selects.length) {
          selectsHtml = `<div class="upw__card-selects">${selects.join("")}</div>`;
        }
      }

      return `
        <div class="upw__card" data-variants="${variantsJson}">
          <a href="${product.url}" class="upw__card-image-link" tabindex="-1">
            ${
              source
                ? `<img src="${source}" alt="${alt}" class="upw__card-image" width="168" height="168" loading="lazy">`
                : '<div class="upw__card-image--empty"></div>'
            }
          </a>
          <div class="upw__card-info">
            <div class="upw__card-meta">
              <a href="${product.url}" class="upw__card-title">${product.title}</a>
              <p class="upw__card-price" data-price>${price}</p>
            </div>
            <div class="upw__card-bottom">
              ${selectsHtml}
              <button class="upw__add" data-variant-id="${defaultVariant.id}" type="button">add to cart \u2014 ${price}</button>
            </div>
          </div>
        </div>`;
    }

    #navigate(direction) {
      const cards = this.#cards();
      if (!cards.length) return;

      const scrollLeft = this.trackOuter.scrollLeft;
      const tolerance = 8;
      let targetCard;

      if (direction > 0) {
        targetCard = cards.find((card) => card.offsetLeft > scrollLeft + tolerance);
      } else {
        targetCard = [...cards]
          .reverse()
          .find((card) => card.offsetLeft < scrollLeft - tolerance);
      }

      const targetLeft = targetCard ? targetCard.offsetLeft : direction > 0 ? this.trackOuter.scrollWidth : 0;
      this.trackOuter.scrollTo({ left: targetLeft, behavior: "smooth" });
      window.setTimeout(() => this.#updateNav(), 220);
    }

    #updateNav() {
      const maxScrollLeft = Math.max(
        this.trackOuter.scrollWidth - this.trackOuter.clientWidth,
        0,
      );
      const scrollLeft = this.trackOuter.scrollLeft;

      this.prevBtn.disabled = scrollLeft <= 4;
      this.nextBtn.disabled = scrollLeft >= maxScrollLeft - 4 || maxScrollLeft === 0;
    }

    #syncLayout() {
      const width = this.trackOuter?.clientWidth || 0;
      this.classList.toggle("upw--wide", width >= 520);
    }

    #handleSelect(select) {
      const card = select.closest(".upw__card");
      const selectedOptions = {};

      card.querySelectorAll(".upw__select").forEach((node) => {
        selectedOptions[Number.parseInt(node.dataset.opt, 10)] = node.value;
      });

      const variants = JSON.parse(card.dataset.variants);
      const match = variants.find((variant) =>
        Object.entries(selectedOptions).every(
          ([index, value]) => variant.options[Number.parseInt(index, 10)] === value,
        ),
      );

      if (match) {
        const price = this.#formatPrice(match.price);
        card.querySelector(".upw__add").dataset.variantId = match.id;
        card.querySelector("[data-price]").textContent = price;
        card.querySelector(".upw__add").textContent = `add to cart \u2014 ${price}`;
      }
    }

    #formatPrice(cents) {
      return (
        "R\u00a0" +
        (cents / 100)
          .toFixed(2)
          .replace(/\.00$/, "")
          .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")
      );
    }

    async #fetchIntent(intent) {
      const response = await fetch(
        `/recommendations/products.json?product_id=${this.productId}&limit=8&intent=${intent}`,
      );
      const data = await response.json();
      return data.products || [];
    }

    async #addItem(button) {
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      if (!variantId) return;

      button.disabled = true;
      const originalText = button.innerHTML;
      button.textContent = "...";

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) throw new Error();

        document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));

        button.textContent = "\u2713";
        setTimeout(() => {
          button.disabled = false;
          button.innerHTML = originalText;
        }, 2000);
      } catch {
        button.disabled = false;
        button.innerHTML = originalText;
      }
    }
  }

  customElements.define("upw-slider", UpwSlider);
})();
